import React, { useState, useRef, useEffect } from 'react';
import {
    BreadcrumbGroup,
    SpaceBetween,
    Button,
    ContentLayout,
    Box,
    Container,
    Header,
    Alert,
    StatusIndicator,
    Icon,
    Badge,
    TextContent,
    ColumnLayout,
    Link,
    Modal,
    Spinner
} from '@amzn/awsui-components-console';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { getUserAtom, userDisplayNameAtom } from '../../contexts/userContext';
import { useProfile } from '../../hooks/useProfiles';
import { useErrorNavigation } from '../../hooks/useErrorNavigation';
import ChatInterface from '../molecules/ChatInterface';
import ExcelPreview from '../molecules/ExcelPreview';
import './ProfileDetail.scss';

interface ProfileDetailProps {
    profileId?: string;
}

interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: string;
    content?: any[][];
    lastModified: number;
    originalFile?: File;
}

const ProfileDetail: React.FC<ProfileDetailProps> = ({ profileId: propProfileId }) => {
    const { id: urlProfileId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { navigateToNotFound } = useErrorNavigation();
    
    // Get user info for avatar
    const authenticatedUser = useAtomValue(getUserAtom);
    const userDisplayName = useAtomValue(userDisplayNameAtom);
    
    // Use URL param if available, otherwise use prop
    const profileId = urlProfileId || propProfileId || '';
    
    // Fetch profile data using API
    const { profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useProfile(profileId);
    
    // State management
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
    
    // Use fetched profile data or fallback
    const profileData = profile ? {
        id: profile.id,
        name: profile.name,
        ldapGroup: profile.ldapGroup,
        description: profile.description || 'No description available',
        createdBy: profile.createdBy || 'Unknown',
        lastModified: profile.lastUpdated || new Date().toISOString(),
        status: profile.status || 'active'
    } : null;

    // Update URL if needed
    useEffect(() => {
        if (!urlProfileId && profileId) {
            // Update URL to include profile ID
            navigate(`/profile/${profileId}`, { replace: true });
        }
    }, [profileId, urlProfileId, navigate]);

    // Predefined questions for quick actions
    const predefinedQuestions = [
        "What are data columns to mapping to be used?",
        "Show me the expense breakdown",
        "Generate monthly financial report",
        "Analyze account reconciliation status",
        "What are the pending transactions?",
        "Show audit trail for last month"
    ];

    // Handle file upload
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        
        // Validate file type
        const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/)) {
            setUploadError('Please upload a CSV or Excel file');
            return;
        }

        setIsUploading(true);
        setUploadError(null);
        
        // Store the actual File object for preview
        setPreviewFile(file);
        setShowPreview(true);

        // Create file object with original file reference
        const uploadedFile: UploadedFile = {
            id: `file-${Date.now()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            originalFile: file  // Store the original File object
        };

        // Add to uploaded files immediately
        setUploadedFiles(prev => [...prev, uploadedFile]);
        setIsUploading(false);
        
        // Clear the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle file removal
    const handleRemoveFile = (fileId: string) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    // Handle template download from S3
    const handleDownloadTemplate = async () => {
        const s3Uri = 's3://addapt-data-beta-us-east-1-956083902883/resources/shared_files/ui/master_template.xlsx';
        
        try {
            setIsDownloadingTemplate(true);
            
            // Get presigned URL for the master template
            const { getPresignedDownloadUrl } = await import('../../utils/s3Download');
            
            // Extract S3 key from URI
            const match = s3Uri.match(/^s3:\/\/[^\/]+\/(.+)$/);
            if (!match || !match[1]) {
                throw new Error('Invalid S3 URI format');
            }
            
            const urlResponse = await getPresignedDownloadUrl(match[1]);
            
            if (!urlResponse?.downloadUrl) {
                throw new Error('Failed to get presigned URL for master template');
            }
            
            // Fetch the template content
            const response = await fetch(urlResponse.downloadUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            
            // Create blob URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = 'master_template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up blob URL
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            console.error('Error downloading master template:', error);
            setUploadError(error.message || 'Failed to download master template');
        } finally {
            setIsDownloadingTemplate(false);
        }
    };

    // Handle view jobs
    const handleViewJobs = () => {
        navigate(`/profile/${profileId}/jobs`);
    };

    // Redirect to error page if profile fails to load - MUST be before any conditional returns
    useEffect(() => {
        if (profileError || (profile && !profileData)) {
            navigateToNotFound('profile', profileId);
        }
    }, [profileError, profile, profileData, navigateToNotFound, profileId]);

    // Show loading state
    if (profileLoading || !profileData) {
        return (
            <div className="profile-detail-page">
                <ContentLayout
                    defaultPadding={true}
                    headerVariant="high-contrast"
                >
                    <Container>
                        <Box textAlign="center" padding={{ vertical: "xl" }}>
                            <Spinner size="large" />
                        </Box>
                    </Container>
                </ContentLayout>
            </div>
        );
    }

    return (
        <div className="profile-detail-page">
            <ContentLayout
                defaultPadding={true}
                headerVariant="high-contrast"
                header={
                    <Box padding={{ horizontal: "xxxl", vertical: "xxs" }}>
                        <div className="profile-header-content">
                            <SpaceBetween size="m" direction="vertical">
                                <BreadcrumbGroup
                                    items={[
                                        { text: "Home", href: "/" },
                                        { text: "My Profile", href: "/profiles" },
                                        { text: profileData.name, href: "#" }
                                    ]}
                                    ariaLabel="Breadcrumbs"
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                    <div style={{ flex: 1 }}>
                                        <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                                            {profileData.name}
                                        </Box>
                                        <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                            {profileData.description}
                                        </Box>

                                        <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                                <Badge color="green">{profileData.ldapGroup || 'No LDAP Group'}</Badge>
                                                <Box color="text-status-inactive">|</Box>
                                                <Link 
                                                    href={`https://phonetool.amazon.com/users/${profileData.createdBy}`}
                                                    fontSize="body-m"
                                                    external={true}
                                                >
                                                    @{profileData.createdBy}
                                                </Link>
                                            </SpaceBetween>
                                        </Box>
                                       
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                                        <Button
                                            iconName="refresh"
                                            variant="normal"
                                            onClick={refreshProfile}
                                            ariaLabel="Refresh profile"
                                            loading={profileLoading}
                                        >
                                        
                                        </Button>
                                        <Button
                                            iconName="download"
                                            onClick={handleDownloadTemplate}
                                            loading={isDownloadingTemplate}
                                            disabled={isDownloadingTemplate}
                                        >
                                            Download Master Template
                                        </Button>
                                        <Button
                                            variant="primary"
                                            iconName="list-view"
                                            onClick={handleViewJobs}
                                        >
                                            View jobs
                                        </Button>
                                    </div>
                                </div>
                            </SpaceBetween>
                        </div>
                    </Box>
                }
            >
                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                />

                <Box>
                    {/* Upload error */}
                    {uploadError && (
                        <Box padding={{ horizontal: "xl", vertical: "l" }}>
                            <Alert
                                type="error"
                                dismissible
                                onDismiss={() => setUploadError(null)}
                                header="Upload failed"
                            >
                                {uploadError}
                            </Alert>
                        </Box>
                    )}

                        {/* Chat Interface with integrated file upload - Full screen */}
                        <ChatInterface
                            profileName={profileData.name}
                            profileId={profileId}
                            predefinedQuestions={predefinedQuestions}
                            uploadedFiles={uploadedFiles}
                            onFileUpload={() => fileInputRef.current?.click()}
                            onRemoveFile={handleRemoveFile}
                            onProcessFile={(file) => {
                                setIsProcessing(true);
                                // Process file logic here
                                setTimeout(() => setIsProcessing(false), 2000);
                            }}
                            useWebSocketControl={true} // Enable WebSocket control for template generation
                            onPreviewFile={(file) => {
                            // Use the original file if available, otherwise try to find it in uploadedFiles
                            if (file.originalFile) {
                                setPreviewFile(file.originalFile);
                                setShowPreview(true);
                            } else {
                                // Try to find the file in uploadedFiles
                                const uploadedFile = uploadedFiles.find(f => f.id === file.id);
                                if (uploadedFile?.originalFile) {
                                    setPreviewFile(uploadedFile.originalFile);
                                    setShowPreview(true);
                                } else {
                                    // Fallback: create a File object from the uploaded file data
                                    const blob = new Blob(['Sample data'], { type: file.type });
                                    const fileObj = new File([blob], file.name, { 
                                        type: file.type,
                                        lastModified: file.lastModified 
                                    });
                                    setPreviewFile(fileObj);
                                    setShowPreview(true);
                                }
                            }
                        }}
                        isProcessing={isProcessing}
                        isUploading={isUploading}
                    userName={userDisplayName || authenticatedUser?.username || 'User'}
                    userInitial={(userDisplayName || authenticatedUser?.username || 'U')[0].toUpperCase()}
                />
                
                {/* Add spacing at the bottom to prevent footer overlap */}
                <div style={{ height: '120px', width: '100%' }}></div>
            </Box>
                
                {/* Excel Preview Modal */}
                <Modal
                    visible={showPreview && previewFile !== null}
                    onDismiss={() => {
                        setShowPreview(false);
                        setPreviewFile(null);
                        // Clear the "Template preview opened" message from chat
                        if ((window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__) {
                            (window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__();
                        }
                    }}
                    size="max"
                    header="Document Preview"
                >
                    {previewFile && (
                        <ExcelPreview 
                            file={previewFile}
                            onClose={() => {
                                setShowPreview(false);
                                setPreviewFile(null);
                                // Clear the "Template preview opened" message from chat
                                if ((window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__) {
                                    (window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__();
                                }
                            }}
                        />
                    )}
                </Modal>
            </ContentLayout>
        </div>
    );
};

export default ProfileDetail;